import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import { SCIFI, armor, glow, glowColor, bake, beam, Gait } from './units-style.js';

// Six legs around a hexagonal hull, at 30°, 90°, 150°... from the nose.
// The feet stay within 0.95 of the 2.0 collision radius at full stride.
const HIP_RADIUS = 0.95;
const HIP_Y = 1.25;
const KNEE = { out: 0.5, up: 0.55 };
const FOOT_OUT = 0.68;
const LEGS = [0, 1, 2, 3, 4, 5].map(i => Math.PI / 6 + i * Math.PI / 3);

// The mortar tube leans forward from its trunnion on the turret
const MORTAR_Y = 2.35;
const MORTAR_TILT = 0.55;
const TUBE_LENGTH = 1.3;

// Siege Drone: a six-legged artillery crawler with team armour, a cluster of
// sensor eyes and a heavy plasma mortar fed by two glowing canisters
export class RocketLauncher {
  constructor(scene, unit, color) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.healthBar = null;
    this.maxHealth = 20; // RocketLauncherHealth from server
    this.geometries = [];
    this.legs = [];
    this.recoil = 0;
    this.timeOffset = Math.random() * 10;
    this.create();
  }

  // Armour and glow parts merged into one mesh each, added to a group
  part(group, parts, glowParts = []) {
    const geometry = bake(parts);
    this.geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, armor());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    if (glowParts.length) {
      const glowGeometry = bake(glowParts);
      this.geometries.push(glowGeometry);
      group.add(new THREE.Mesh(glowGeometry, glow()));
    }
    return mesh;
  }

  create() {
    const bodyGroup = new THREE.Group();
    const team = new THREE.Color(this.color);
    const trim = glowColor(this.color);
    const plasma = glowColor(this.color, 0.2);

    // Hull and turret bob together while the legs carry them
    this.body = new THREE.Group();
    bodyGroup.add(this.body);

    // Hexagonal hull: gunmetal belly, team armour shell, white rim and seams
    const hull = [
      colored(new THREE.CylinderGeometry(1.15, 0.85, 0.5, 6), SCIFI.gunmetal, { y: 1.15 }),
      colored(new THREE.CylinderGeometry(1.24, 1.24, 0.12, 6), SCIFI.white, { y: 1.45 }),
      colored(new THREE.CylinderGeometry(0.95, 1.2, 0.36, 6), team, { y: 1.69 }),
      colored(new THREE.CylinderGeometry(0.8, 0.95, 0.1, 6), SCIFI.alloyDark, { y: 1.92 }),
      // Sensor head jutting from the nose
      colored(new THREE.BoxGeometry(0.62, 0.36, 0.4), SCIFI.alloy, { y: 1.42, z: 1.18 }),
      colored(new THREE.BoxGeometry(0.56, 0.2, 0.06), SCIFI.black, { y: 1.44, z: 1.39 }),
      // Turret ring and mortar cradle
      colored(new THREE.CylinderGeometry(0.62, 0.7, 0.26, 8), SCIFI.gunmetal, { y: 2.08 }),
      colored(new THREE.BoxGeometry(0.16, 0.6, 0.7), team, { x: -0.48, y: 2.4, z: -0.05 }),
      colored(new THREE.BoxGeometry(0.16, 0.6, 0.7), team, { x: 0.48, y: 2.4, z: -0.05 }),
      colored(new THREE.BoxGeometry(0.18, 0.08, 0.72), SCIFI.white, { x: -0.48, y: 2.72, z: -0.05 }),
      colored(new THREE.BoxGeometry(0.18, 0.08, 0.72), SCIFI.white, { x: 0.48, y: 2.72, z: -0.05 }),
      colored(new THREE.CylinderGeometry(0.12, 0.12, 1.12, 6), SCIFI.alloyDark, { y: MORTAR_Y, rz: Math.PI / 2 }),
      // Canister racks at the back and a whip antenna
      colored(new THREE.BoxGeometry(0.9, 0.12, 0.42), SCIFI.alloyDark, { y: 2.02, z: -0.72 }),
      colored(new THREE.CylinderGeometry(0.03, 0.04, 1.4, 4), SCIFI.alloy, { x: 0.55, y: 2.6, z: -0.55 }),
      colored(new THREE.BoxGeometry(0.22, 0.14, 0.22), SCIFI.gunmetal, { x: 0.55, y: 1.96, z: -0.55 })
    ];
    for (const x of [-0.24, 0.24]) {
      hull.push(colored(new THREE.CylinderGeometry(0.17, 0.17, 0.08, 8), SCIFI.gunmetal, { x, y: 2.12, z: -0.72 }));
      hull.push(colored(new THREE.CylinderGeometry(0.17, 0.17, 0.08, 8), SCIFI.gunmetal, { x, y: 2.62, z: -0.72 }));
    }
    for (let i = 0; i < 6; i++) {
      hull.push(colored(new THREE.BoxGeometry(0.04, 0.37, 0.05), SCIFI.black, { y: 1.69, z: 1.08 }).rotateY(i * Math.PI / 3));
    }
    const hullGlow = [
      // Three sensor eyes and a waist band
      colored(new THREE.SphereGeometry(0.08, 6, 4), SCIFI.cyan, { x: -0.18, y: 1.46, z: 1.4 }),
      colored(new THREE.SphereGeometry(0.11, 6, 4), trim, { y: 1.44, z: 1.41 }),
      colored(new THREE.SphereGeometry(0.08, 6, 4), SCIFI.cyan, { x: 0.18, y: 1.46, z: 1.4 }),
      colored(new THREE.CylinderGeometry(1.16, 1.16, 0.05, 6, 1, true), trim, { y: 1.3 }),
      colored(new THREE.CylinderGeometry(0.71, 0.71, 0.05, 8, 1, true), trim, { y: 2.14 })
    ];
    for (const sx of [-1, 1]) {
      hullGlow.push(colored(new THREE.BoxGeometry(0.04, 0.3, 0.46), trim, { x: sx * 0.57, y: 2.4, z: -0.05 }));
    }
    this.part(this.body, hull, hullGlow);

    // Plasma canisters, whose cores pulse and flare when the mortar fires
    this.cores = [];
    const coreGeometry = bake([colored(new THREE.CylinderGeometry(0.13, 0.13, 0.42, 8), plasma, {})]);
    this.geometries.push(coreGeometry);
    for (const x of [-0.24, 0.24]) {
      const core = new THREE.Mesh(coreGeometry, glow());
      core.position.set(x, 2.37, -0.72);
      this.body.add(core);
      this.cores.push(core);
    }

    // Blinking antenna tip
    const tipGeometry = bake([colored(new THREE.BoxGeometry(0.08, 0.08, 0.08), trim, {})]);
    this.geometries.push(tipGeometry);
    this.beacon = new THREE.Mesh(tipGeometry, glow());
    this.beacon.position.set(0.55, 3.32, -0.55);
    this.body.add(this.beacon);

    // Mortar on its trunnion: a thick tube leaning forward, ringed with coils,
    // in a recoil group that slides back down the tube's axis
    this.mortar = new THREE.Group();
    this.mortar.position.set(0, MORTAR_Y, 0);
    this.mortar.rotation.x = MORTAR_TILT;
    this.body.add(this.mortar);
    this.recoilGroup = new THREE.Group();
    this.mortar.add(this.recoilGroup);
    const tube = [
      colored(new THREE.BoxGeometry(0.62, 0.5, 0.62), SCIFI.alloy, { y: 0.05 }),
      colored(new THREE.BoxGeometry(0.64, 0.12, 0.64), team, { y: 0.24 }),
      colored(new THREE.CylinderGeometry(0.27, 0.31, TUBE_LENGTH, 8), SCIFI.alloyDark, { y: TUBE_LENGTH / 2 + 0.1 }),
      colored(new THREE.CylinderGeometry(0.34, 0.3, 0.2, 8), SCIFI.gunmetal, { y: TUBE_LENGTH + 0.12 })
    ];
    const tubeGlow = [];
    for (const y of [0.62, 0.86, 1.1]) {
      tubeGlow.push(colored(new THREE.CylinderGeometry(0.33, 0.33, 0.06, 8), trim, { y }));
    }
    tubeGlow.push(colored(new THREE.CylinderGeometry(0.22, 0.22, 0.04, 8), plasma, { y: TUBE_LENGTH + 0.2 }));
    this.part(this.recoilGroup, tube, tubeGlow);

    // Muzzle flash, hidden until the mortar fires
    const flashGeometry = bake([colored(new THREE.IcosahedronGeometry(0.3, 0), plasma, {})]);
    this.geometries.push(flashGeometry);
    this.flash = new THREE.Mesh(flashGeometry, glow());
    this.flash.position.y = TUBE_LENGTH + 0.4;
    this.flash.visible = false;
    this.recoilGroup.add(this.flash);

    // Six jointed legs; each group pivots at its hip
    for (let i = 0; i < LEGS.length; i++) {
      const a = LEGS[i];
      const leg = new THREE.Group();
      leg.position.set(Math.sin(a) * HIP_RADIUS, HIP_Y, Math.cos(a) * HIP_RADIUS);
      leg.rotation.y = a;
      // Alternating tripods: every other leg steps together
      leg.userData = { offset: i % 2 ? Math.PI : 0 };
      this.part(leg, this.buildLeg(team), [
        colored(new THREE.CylinderGeometry(0.2, 0.2, 0.04, 6), trim, { y: -HIP_Y + 0.02, z: FOOT_OUT })
      ]);
      bodyGroup.add(leg);
      this.legs.push(leg);
    }

    // Create health bar just above the antenna
    this.healthBar = new HealthBar(this.maxHealth, 2.6, 0.28);
    this.healthBar.getGroup().position.y = 3.85;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.gait = new Gait(this.mesh);

    this.scene.add(this.mesh);
  }

  // One leg reaching outward along its local +Z: hip, raised knee, foot on the ground
  buildLeg(team) {
    const knee = [0, KNEE.up, KNEE.out];
    const foot = [0, -HIP_Y + 0.1, FOOT_OUT];
    return [
      colored(new THREE.SphereGeometry(0.18, 6, 4), SCIFI.gunmetal, {}),
      beam([0, 0, 0], knee, 0.28, SCIFI.white),
      beam([0, 0.12, 0.04], [0, KNEE.up + 0.12, KNEE.out - 0.02], 0.1, team),
      colored(new THREE.SphereGeometry(0.15, 6, 4), SCIFI.gold, { y: knee[1], z: knee[2] }),
      beam(knee, foot, 0.24, SCIFI.alloyDark),
      colored(new THREE.CylinderGeometry(0.14, 0.19, 0.16, 6), SCIFI.gunmetal, { y: foot[1], z: foot[2] })
    ];
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.1;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Tripod gait driven by distance moved, idle canister pulse, and the
  // recoil and flash that follow a shot
  animate() {
    const dt = this.gait.step(3, 2.2);
    if (dt <= 0) return;
    const { walk, phase } = this.gait;
    const t = performance.now() / 1000 + this.timeOffset;

    for (const leg of this.legs) {
      const p = phase + leg.userData.offset;
      // Swing about the hip (local Y) and lift the foot on the forward swing
      leg.children.forEach(child => { child.rotation.y = Math.sin(p) * 0.3 * walk; });
      leg.position.y = HIP_Y + Math.max(0, Math.cos(p)) * 0.22 * walk;
    }
    this.body.position.y = Math.abs(Math.sin(phase)) * 0.05 * walk + Math.sin(t * 1.4) * 0.03 * (1 - walk);

    // The mortar slams back down its axis, then eases out
    this.recoil = Math.max(0, this.recoil - dt * 2.5);
    const kick = this.recoil * this.recoil;
    this.recoilGroup.position.y = -0.35 * kick;
    this.flash.visible = this.recoil > 0.55;
    this.flash.scale.setScalar(0.6 + (1 - this.recoil) * 1.6);
    this.flash.rotation.y = t * 4;

    const pulse = 1 + Math.sin(t * 4.2) * 0.06 + kick * 0.5;
    for (const core of this.cores) core.scale.set(pulse, 1, pulse);
    this.beacon.visible = Math.sin(t * 3) > -0.4;
  }

  // Called when this drone fires: recoil and flash
  fire() {
    this.recoil = 1;
  }

  // Set target position for aiming
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    // Materials are shared; only geometries are ours
    this.geometries.forEach(g => g.dispose());
    this.geometries = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
